"use client";
import { usePathname } from 'next/navigation';
import { StudentSidebar } from '@/modules/student/components/student-sidenav';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';

// Public paths (login, signup) should not render the sidebar.
const PUBLIC_PATH_PREFIXES = [
  '/student/login',
  '/student/np/login',
  '/student/img/login',
  '/student/pa/login',
  '/student/signup',
  '/student/np/signup',
  '/student/img/signup',
  '/student/pa/signup',
  '/student/onboarding',
];

function isPublicPath(pathname: string): boolean {
  // Exact root '/student' should be public
  if (pathname === '/student') return true;
  // Check if path matches any of the public prefixes
  return PUBLIC_PATH_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'));
}

export default function StudentLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // IMG students have their own dedicated portal chrome under /student/img/* — don't
  // render the NP student sidebar there.
  const isImg = pathname.startsWith('/student/img');
  const showSidebar = !isPublicPath(pathname) && !isImg;

  if (!showSidebar) {
    return (
      <main className="w-full">
        {children}
      </main>
    );
  }

  return (
    <SidebarProvider>
      <StudentSidebar />
      <SidebarInset className="bg-slate-50">
        {/* Mobile-only top bar with hamburger */}
        <header className="md:hidden sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4">
          <SidebarTrigger className="-ml-1" />
          <span className="font-semibold text-slate-900">Student Portal</span>
        </header>
        <div className="min-w-0 flex-1 px-3 py-4 md:px-0 md:py-0">
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

